import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import axios from 'axios';
import ErrorBoundary from '../../../components/ErrorBoundary';
import ComponentShowcase from '../../../components/ComponentShowcase';
import Projects from './Projects';
import Dashboard from './Dashboard';
import ComplianceDashboard from './ComplianceDashboard';
import Connectors from './Connectors';
import Controls from './Controls';
import ControlDetail from './ControlDetail';
import Evidence from './Evidence';
import OldFindings from './OldFindings';
import FindingsSummary from './FindingsSummary';
import FindingsDetails from './FindingsDetails';
import DetectionPlan from './DetectionPlan';
import ControlResults from './ControlResults';
import Cases from './Cases';
import Alerts from './Alerts';
import Reports from './Reports';
import Settings from './Settings';
import AuditTrail from './AuditTrail';
import MSPDashboard from './MSPDashboard';
import OnboardingWizard from './OnboardingWizard';
import Scans from './Scans';
import Diagnostics from './Diagnostics';

function TrustGrid({ onLogout }) {
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [checkingOnboarding, setCheckingOnboarding] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    checkOnboardingRequired();
  }, []);

  const checkOnboardingRequired = async () => {
    try {
      const res = await axios.get('/api/trustgrid/v1/onboarding/check');
      setShowOnboarding(res.data.required);
    } catch (error) {
      console.error('Failed to check onboarding status:', error);
      // If API fails, don't show onboarding
      setShowOnboarding(false);
    } finally {
      setCheckingOnboarding(false);
    }
  };

  const handleOnboardingComplete = (wizardData) => {
    setShowOnboarding(false);
    // Navigate to the project dashboard
    navigate('/trustgrid/dashboard');
  };

  const handleOnboardingSkip = () => {
    setShowOnboarding(false);
    navigate('/trustgrid/projects');
  };

  if (checkingOnboarding) {
    return <div>Loading...</div>;
  }

  return (
    <ErrorBoundary>
      <Routes>
        {/* Overview */}
        <Route path="msp-dashboard" element={<MSPDashboard />} />
        <Route path="dashboard" element={<Dashboard />} />

        {/* Compliance */}
        <Route path="compliance" element={<ComplianceDashboard />} />
        <Route path="control-results" element={<ControlResults />} />

        {/* Risk Management */}
        <Route path="findings" element={<Navigate to="/trustgrid/findings/summary" replace />} />
        <Route path="findings/summary" element={<FindingsSummary />} />
        <Route path="findings/details" element={<FindingsDetails />} />
        <Route path="findings/detection-plan" element={<DetectionPlan />} />
        <Route path="old-findings" element={<OldFindings />} />
        <Route path="cases" element={<Cases />} />
        <Route path="alerts" element={<Alerts />} />

        {/* Configuration */}
        <Route path="projects" element={<Projects />} />
        <Route path="connectors" element={<Connectors />} />
        <Route path="controls" element={<Controls />} />
        <Route path="controls/:controlCode" element={<ControlDetail />} />
        <Route path="settings" element={<Settings />} />

        {/* Data */}
        <Route path="scans" element={<Scans />} />
        <Route path="evidence" element={<Evidence />} />

        {/* Reporting */}
        <Route path="reports" element={<Reports />} />

        {/* System */}
        <Route path="audit-trail" element={<AuditTrail />} />
        <Route path="diagnostics" element={<Diagnostics />} />

        {/* Component Showcase (Development) */}
        <Route path="showcase" element={<ComponentShowcase />} />

        {/* Default redirects */}
        <Route path="" element={<Navigate to="dashboard" replace />} />
        <Route path="*" element={<Navigate to="dashboard" replace />} />
      </Routes>

      {/* Onboarding Wizard */}
      <OnboardingWizard
        visible={showOnboarding}
        onComplete={handleOnboardingComplete}
        onSkip={handleOnboardingSkip}
      />
    </ErrorBoundary>
  );
}

export default TrustGrid;
